export const PRAYERS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
export type PrayerId = (typeof PRAYERS)[number];

/**
 * The four qualities are recorded INDEPENDENTLY, not as a ladder: praying in
 * the masjid but after its time is a real combination and has to be
 * representable. The single dependency is that the first takbeerah requires
 * jamaah — you cannot catch it alone at home.
 */
export interface Mark {
  prayed: boolean;
  onTime: boolean;
  jamaah: boolean;
  takbirah: boolean;
}

export const EMPTY_MARK: Mark = { prayed: false, onTime: false, jamaah: false, takbirah: false };

export type QualityKey = keyof Mark;

export interface Quality {
  key: QualityKey;
  /** Segment index in the mark: 1 = top, 2 = right, 3 = bottom, 4 = left. */
  segment: 1 | 2 | 3 | 4;
  label: string;
  shortLabel: string;
  ar: string;
  kidsHint: string;
  adultHint: string;
}

export const QUALITIES: readonly Quality[] = [
  {
    key: 'prayed',
    segment: 1,
    label: 'I prayed it',
    shortLabel: 'Prayed',
    ar: 'صليتها',
    kidsHint: 'the first and biggest step',
    adultHint: 'the prayer was performed',
  },
  {
    key: 'onTime',
    segment: 2,
    label: 'In its time',
    shortLabel: 'On time',
    ar: 'في وقته',
    kidsHint: 'before the next adhān called',
    adultHint: 'within the prescribed window',
  },
  {
    key: 'jamaah',
    segment: 3,
    label: 'In the masjid, in jamā‘ah',
    shortLabel: 'Jamā‘ah',
    ar: 'جماعة',
    kidsHint: 'shoulder to shoulder in the rows',
    adultHint: 'in congregation at the masjid',
  },
  {
    key: 'takbirah',
    segment: 4,
    label: 'Caught the first takbīrah',
    shortLabel: '1st takbīrah',
    ar: 'التكبيرة الأولى',
    kidsHint: 'in the row before the imām began',
    adultHint: 'present for the opening takbīr',
  },
] as const;

/** Enforce the two invariants, whatever the caller passed. */
export function normaliseMark(m: Mark): Mark {
  if (!m.prayed) return EMPTY_MARK;
  if (!m.jamaah) return { ...m, takbirah: false };
  return m;
}

export function isQualityEnabled(m: Mark, key: QualityKey): boolean {
  if (key === 'prayed') return true;
  if (key === 'takbirah') return m.prayed && m.jamaah;
  return m.prayed;
}

export function toggleQuality(m: Mark, key: QualityKey): Mark {
  return normaliseMark({ ...m, [key]: !m[key] });
}

export function isEmptyMark(m: Mark): boolean {
  return !m.prayed && !m.onTime && !m.jamaah && !m.takbirah;
}

/** Classes the design system uses to light the mark's segments. */
export function markClasses(m: Mark): string {
  const n = normaliseMark(m);
  return QUALITIES.filter((q) => n[q.key])
    .map((q) => `on${q.segment}`)
    .join(' ');
}

export const NOTE_KEYS = [
  'note.later',
  'note.missed',
  'note.takbirah',
  'note.jamaahOnTime',
  'note.jamaahLate',
  'note.onTime',
  'note.late',
] as const;

export type NoteKey = (typeof NOTE_KEYS)[number];

/**
 * Which note a mark deserves. Returns a key rather than prose: the rule is
 * domain, the words are translation.
 */
export function markNoteKey(m: Mark, due: boolean): NoteKey {
  const n = normaliseMark(m);
  if (!n.prayed) return due ? 'note.missed' : 'note.later';
  if (n.takbirah) return 'note.takbirah';
  if (n.jamaah && n.onTime) return 'note.jamaahOnTime';
  if (n.jamaah) return 'note.jamaahLate';
  if (n.onTime) return 'note.onTime';
  return 'note.late';
}

export function isPrayerId(value: string): value is PrayerId {
  return (PRAYERS as readonly string[]).includes(value);
}
